export const SURVEY_QUESTIONS = {
  q01: ['Yes', 'No', "I wasn't aware there was a trail"],
  q02: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree', 'Not Sure'],
  q03: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree', 'Not Sure'],
  q04: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree', 'Not Sure'],
  q05: ['Yes', 'No', 'Not Sure'],
  q06: ['Yes', 'No', 'Not Sure'],
  q07: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree', 'Not Sure'],
  q08: ['Frequently', 'Occasionally', 'Rarely', 'Never', 'Not Sure'],
  q09: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree', 'Not Sure'],
  q10: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree', 'Not Sure'],
  q11: ['Strongly Support', 'Support', 'Do Not Support', 'Strongly Do Not Support', 'Not Sure / Depends'],
  q12: ['Yes', 'No'],
  q13: ['Strongly Support', 'Support', 'Do Not Support', 'Strongly Do Not Support', 'Not Sure'],
} as const;

export type QuestionId = keyof typeof SURVEY_QUESTIONS;
export type SurveyAnswers = Partial<Record<QuestionId, string>>;

export const SURVEY_QUESTION_TEXT: Record<QuestionId, string> = {
  q01: 'Have you used the forest trail in Petrovitsky Park?',
  q02: 'The undeveloped forest and trail at Petrovitsky Park is worth preserving',
  q03: 'I currently use or would use the trail',
  q04: 'I would enjoy the trail less if portions of the forest understory (brush, ferns, etc.) were visibly thinned or cleared.',
  q05: 'Should efforts be made to preserve this as a completely natural photograph opportunity?',
  q06: 'Do you feel walking through these ferns should be avoided to minimize damage to the fronds?',
  q07: 'I (would) walk the trail to enjoy a nature escape',
  q08: 'I would use a park bench if installed in the forest, next to the trail',
  q09: 'The park should make greater efforts to keep the trail experience feeling natural',
  q10: 'I would support converting the disturbed area into a minor amenity such as a picnic table, small footprint play area, or similar.',
  q11: 'Would you support minor improvements that minimally disturb the forest along this area?',
  q12: 'Have you already heard arguments for / against the disc golf course in Petrovitsky Park?',
  q13: 'Do you support moving forward with this disc golf course?',
};

export const QUESTION_IDS = Object.keys(SURVEY_QUESTIONS) as QuestionId[];
export const SURVEY_VARIANTS = [
  {
    key: 'original',
    label: 'Original survey',
    filename: 'Survey.hvy',
    documentPath: '/surveys/original.hvy',
  },
  {
    key: 'no-mitigation',
    label: 'No mitigation',
    filename: 'Survey - no mitigation rebuttal.hvy',
    documentPath: '/surveys/no-mitigation.hvy',
  },
  {
    key: 'pro-biased',
    label: 'Biased towards pro',
    filename: 'Survey - biased towards pro.hvy',
    documentPath: '/surveys/pro-biased.hvy',
  },
] as const;

export type SurveyVariant = (typeof SURVEY_VARIANTS)[number];
export type SurveyKey = SurveyVariant['key'];

export interface SurveyCatalogEntry {
  key: SurveyKey;
  id: string;
  version: number;
  label: string;
  documentPath: string;
  questionIds: readonly QuestionId[];
  questionCount: number;
}

export const SMOKE_TEST_NEIGHBORHOOD = 'smoke-test';
export const SMOKE_TEST_NEIGHBORHOODS: ReadonlySet<string> = new Set([
  SMOKE_TEST_NEIGHBORHOOD,
  'Fairwood Market 8/12',
]);

export function isSmokeTestNeighborhood(neighborhood: string): boolean {
  return SMOKE_TEST_NEIGHBORHOODS.has(neighborhood);
}

export interface SubmissionPayload {
  submissionId: string;
  surveyId: string;
  surveyVersion: number;
  accessCode: string;
  answers: SurveyAnswers;
  feedback?: string;
}

export function normalizeAccessCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
}

export function isAccessCode(value: string): boolean {
  return /^[A-Z0-9]{7}$/.test(value);
}

export function validateAnswers(value: unknown, questionIds: readonly QuestionId[] = QUESTION_IDS): value is SurveyAnswers {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const answers = value as Record<string, unknown>;
  return questionIds.every((questionId) => {
    const answer = answers[questionId];
    return typeof answer === 'string' && (SURVEY_QUESTIONS[questionId] as readonly string[]).includes(answer);
  }) && Object.keys(answers).length === questionIds.length;
}

export function missingQuestionIds(answers: SurveyAnswers, questionIds: readonly QuestionId[] = QUESTION_IDS): QuestionId[] {
  return questionIds.filter((questionId) => !(SURVEY_QUESTIONS[questionId] as readonly string[]).includes(answers[questionId] ?? ''));
}
